import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_12, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';

import {buildConditionTooltip, EdgeConditionStatus} from './EdgeConditionConstants';
import {EdgeCondition} from './EdgeConditionTypes';

const STATUS_TO_BG: Record<EdgeConditionStatus, string> = {
  [EdgeConditionStatus.UNCHECKED]: Colors.GRAY_LIGHTER,
  [EdgeConditionStatus.PASSED]: Colors.GREEN_LIGHTEST,
  [EdgeConditionStatus.FAILED]: Colors.RED_POWDER,
};

const STATUS_TO_COLOR: Record<EdgeConditionStatus, string> = {
  [EdgeConditionStatus.UNCHECKED]: Colors.GRAY_SEMIDARK,
  [EdgeConditionStatus.PASSED]: Colors.GREEN_DARKER,
  [EdgeConditionStatus.FAILED]: Colors.RED_DARK,
};

interface EdgeConditionLabelProps {
  condition: EdgeCondition;
  status: EdgeConditionStatus;
  targetStepTypeName: string;
  branchLabel: string;
}

export default function EdgeConditionLabel({
  condition,
  status,
  targetStepTypeName,
  branchLabel,
}: EdgeConditionLabelProps): JSX.Element {
  const tooltipText = buildConditionTooltip(condition, targetStepTypeName);

  return (
    <Tooltip text={tooltipText}>
      <div
        className={css(styles.wrapper)}
        {...getTestProps({component: 'EdgeConditionLabel', element: 'pill'})}
      >
        <div
          className={css(styles.diamond)}
          style={{
            backgroundColor: STATUS_TO_BG[status],
            borderColor: STATUS_TO_COLOR[status],
          }}
        />
        {branchLabel !== '' && (
          <span className={css(styles.letter)} style={{color: STATUS_TO_COLOR[status]}}>
            {branchLabel}
          </span>
        )}
      </div>
    </Tooltip>
  );
}

const WRAPPER_SIZE = 28;
const DIAMOND_INNER = 18;

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative',
    width: WRAPPER_SIZE,
    height: WRAPPER_SIZE,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'default',
    pointerEvents: 'all',
  },
  diamond: {
    position: 'absolute',
    width: DIAMOND_INNER,
    height: DIAMOND_INNER,
    transform: 'rotate(45deg)',
    borderWidth: 1.5,
    borderStyle: 'solid',
    borderRadius: 2,
  },
  letter: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    position: 'relative',
    lineHeight: 1,
  },
});
